import { Injectable, inject } from "@angular/core";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { AppConfigService } from "@api/services/app-config.service";
import { TokenService } from "@auth/services/token/token.service";
import { AuthService } from "@auth/services/auth/auth.service";

@Injectable({
    providedIn: "root",
})
export class AppInsightsService {
    private readonly configService = inject(AppConfigService);
    private readonly authService = inject(AuthService);
    private readonly tokenService = inject(TokenService);

    private appInsights!: ApplicationInsights;
    private isUserLoggedIn = false;

    initialize() {
        const config = this.configService.appConfig(); // this is a signal

        if (!config?.appInsightsConnectionString) {
            console.warn("Application Insights connection string is not configured.");
            return;
        }

        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: config.appInsightsConnectionString,
                enableAutoRouteTracking: true,
            },
        });

        this.appInsights.loadAppInsights();
        this.setUserContext();

        window.addEventListener("unhandledrejection", (event) => {
            const reason = event.reason instanceof Error ? event.reason : new Error(event.reason);
            this.logException(reason);
        });
    }

    setUserContext() {
        const token = this.tokenService.getToken();

        if (!this.appInsights || !this.authService.isAuthorized() || !token) return;

        // Set authenticated user ID
        if (token.sub) {
            this.appInsights.setAuthenticatedUserContext(token.sub);
            this.isUserLoggedIn = true;
        }

        const userId = token.sub ?? "Unknown User";
        const userName = token.userEmail ?? "Unknown User";
        const tenant = token.tenant ?? "Unknown Tenant";
        const centerId = token.centerId ?? 0;

        // Add custom properties that will be included with all telemetry
        this.appInsights.addTelemetryInitializer((envelope) => {
            // Only add user properties if user is still logged in
            if (this.isUserLoggedIn && envelope.baseData) {
                envelope.baseData["properties"] = envelope.baseData["properties"] || {};
                envelope.baseData["properties"]["UserId"] = userId;
                envelope.baseData["properties"]["Username"] = userName;
                envelope.baseData["properties"]["Tenant"] = tenant;
                envelope.baseData["properties"]["CenterId"] = centerId;
            }

            return true;
        });
    }

    setCenterContext(scopedCenterId: number) {
        if (!this.appInsights) return;

        // Add custom properties that will be included with all telemetry
        this.appInsights.addTelemetryInitializer((envelope) => {
            if (envelope.baseData) {
                envelope.baseData["properties"]["ScopedCenterId"] = scopedCenterId;
            }

            return true;
        });
    }

    // Clear context on logout
    clearContext() {
        if (!this.appInsights) return;

        // Prevent user properties on future telemetry
        this.appInsights.clearAuthenticatedUserContext();
        this.isUserLoggedIn = false;
    }

    logPageView(name: string) {
        this.appInsights?.trackPageView({ name });
    }

    logTrace(message: string) {
        this.appInsights?.trackTrace({ message });
    }

    logEvent(name: string, properties?: Record<string, string | number | boolean | undefined>) {
        this.appInsights?.trackEvent({ name }, properties);
    }

    logException(error: Error, properties?: Record<string, string | number | boolean | undefined>) {
        this.appInsights?.trackException({ exception: error, properties });
    }
}
